import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { ClientOrderBy, LastVisitBucket, Note, TeamMeetingState } from "@acto/gql/graphql";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useCustomersManagement } from "@acto/taskpane/core/data-access/customers/useCustomersManagement";
import { ManagementDataTable } from "../ui/DataTable/ManagementDataTable";
import { ManagementDataTableSkeleton } from "../ui/DataTable/ManagementDataTableSkeleton";
import type { Column, Row } from "../ui/DataTable/types";
import { type KPIHeaderColumn, KPIHeaders } from "../ui/KPIHeaders/KPIHeaders";
import { KPIHeadersSkeleton } from "../ui/KPIHeaders/KPIHeadersSkeleton";
import { PageHeader } from "../ui/PageHeader/PageHeader";
import { ManagementSearchAndFilterBar } from "../ui/SearchAndFilterBar/ManagementSearchAndFilterBar";
import { CustomerFilterBar } from "../ui/CustomerFilterBar";
import { Flex } from "@acto/taskpane/core/components/flex/Flex";
import { useDebouncedSearchTerm } from "@acto/taskpane/core/utils/hooks/useDebouncedSearchTerm";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import { useCascadingPeriod } from "../hooks/useCascadingPeriod";
import { ManagementSidepanel } from "../ui/Sidepanel/ManagementSidepanel";
import { CustomerDetailsSidePanel } from "../ui/SidePanelContent/CustomerDetailsSidePanel/CustomerDetailsSidePanel";
import { CustomerRecapsSidePanel } from "../ui/SidePanelContent/CustomerRecapsSidePanel";
import { TodoSidePanel } from "../ui/SidePanelContent/TodoSidePanel";
import {
  APPOINTMENT_STATE_BY_ACTIVITY,
  type ActivityType,
  EmployeeSidePanel,
} from "../ui/SidePanelContent/EmployeeSidePanel/EmployeeSidePanel";
import { EmployeeAppointmentStateSidePanel } from "../ui/SidePanelContent/AppointmentStateSidePanel";
import { DismissedCustomersSidePanel } from "../ui/SidePanelContent/DismissedCustomersSidePanel";
import { EmployeeMarkedDoneSidePanel } from "../ui/SidePanelContent/MarkedDoneSidePanel";
import { OpenTodosSidePanel, OpenTodosSidePanelSkeleton } from "../ui/SidePanelContent/OpenTodosSidePanel";
import { useCustomerById } from "@acto/taskpane/core/data-access/customers/useCustomerById";
import { useOpenTodosData } from "@acto/taskpane/core/data-access/team/useOpenTodosData";
import TeamEmployee, { KpiValue } from "@acto/taskpane/core/domain/TeamEmployee";
import { DateRange, PeriodOption } from "../ui/DateRangeSelector/types";
import { isComparableWindow, periodToDateRange } from "../utils/periodToDateRange";
import { DateRangeSelector } from "../ui/DateRangeSelector/DateRangeSelector";
import { PaddingWrapper, Divider } from "../ui/layout";
import { formatCurrency, formatChange, formatProfitWithMargin } from "../utils/formatters";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { KpiComparisonMode } from "@acto/taskpane/core/domain";
import { formatDate, formatComparisonRange } from "../utils/dateFormatters";
import { useSortHandler } from "../hooks/useSortHandler";
import { useOrderBy } from "../hooks/useOrderBy";
import { MANAGEMENT_TODO_TYPES } from "../utils/managementTodoTypes";
import {
  type ProductDrillRoot,
  type ProductGroupRef,
  ProductDrilldownSidePanel,
} from "../ui/SidePanelContent/ProductDrilldownSidePanel";
import type { CustomerMetric } from "../ui/SidePanelContent/CustomerDetailsSidePanel/customerMetric";

const ExternalReference = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[56],
}));

const CustomerNameCell = ({ name, externalReference }: { name: string; externalReference: string }) => (
  <>
    {name}
    {externalReference && (
      <>
        <br />
        <ExternalReference>#{externalReference}</ExternalReference>
      </>
    )}
  </>
);

const columnKeyToOrderBy: Record<string, { asc: ClientOrderBy; desc: ClientOrderBy }> = {
  name: { asc: ClientOrderBy.NameAsc, desc: ClientOrderBy.NameDesc },
  revenue: {
    asc: ClientOrderBy.RevenueAsc,
    desc: ClientOrderBy.RevenueDesc,
  },
  profit: { asc: ClientOrderBy.ProfitAsc, desc: ClientOrderBy.ProfitDesc },
  orderFrequency: {
    asc: ClientOrderBy.OrderFrequencyAsc,
    desc: ClientOrderBy.OrderFrequencyDesc,
  },
  employee: {
    asc: ClientOrderBy.EmployeeAsc,
    desc: ClientOrderBy.EmployeeDesc,
  },
  lastVisit: {
    asc: ClientOrderBy.LastVisitAsc,
    desc: ClientOrderBy.LastVisitDesc,
  },
};

export const CustomersPage = () => {
  const t = useTranslations("customers");
  const tKpi = useTranslations("kpi");
  const comparisonMode = useComparisonModeContext();
  const subtitle =
    comparisonMode === KpiComparisonMode.PERIOD_OVER_PERIOD ? t("subtitlePeriodOverPeriod") : t("subtitleYearOverYear");
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const { sortColumn, sortDirection, handleSort } = useSortHandler("revenue", "desc");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  type CustomerActivity = "productGroup" | "completedRecaps";
  const [activeActivity, setActiveActivity] = useState<CustomerActivity | null>(null);
  const [activityContext, setActivityContext] = useState<string | null>(null);
  const [level3ClientId, setLevel3ClientId] = useState<string | null>(null);
  const [productDrillRoot, setProductDrillRoot] = useState<ProductDrillRoot | null>(null);
  const {
    displayValue: searchValue,
    setDisplayValue: setSearchValue,
    debouncedValue: debouncedSearchTerm,
  } = useDebouncedSearchTerm();

  const track = useTrack();

  const [selectedEmployeeInfo, setSelectedEmployeeInfo] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const selectedEmployeeId = selectedEmployeeInfo?.id ?? null;
  const [activeEmployeeActivity, setActiveEmployeeActivity] = useState<ActivityType | null>(null);
  const [employeeLevel3ClientId, setEmployeeLevel3ClientId] = useState<string | null>(null);
  const activeAppointmentState = activeEmployeeActivity
    ? (APPOINTMENT_STATE_BY_ACTIVITY[activeEmployeeActivity] ?? null)
    : null;

  const [selectedPeriod, setSelectedPeriod] = useState(PeriodOption.THIS_MONTH);
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);

  const effectiveDateRange = useMemo(
    () => periodToDateRange(selectedPeriod, customDateRange),
    [selectedPeriod, customDateRange],
  );

  const showTrends = isComparableWindow(selectedPeriod, customDateRange);

  const [assignedEmployeeId, setAssignedEmployeeId] = useState<string | null>(null);
  const [revenueRange, setRevenueRange] = useState<{
    min: number | null;
    max: number | null;
  }>({
    min: null,
    max: null,
  });
  const [lastVisitBucket, setLastVisitBucket] = useState<LastVisitBucket | null>(null);
  const [lastVisitRange, setLastVisitRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({
    startDate: null,
    endDate: null,
  });

  const customerPeriod = useCascadingPeriod(selectedPeriod, customDateRange, selectedCustomerId);

  const productGroupPeriod = useCascadingPeriod(customerPeriod.period, customerPeriod.customDateRange, activeActivity);

  const recapsPeriod = useCascadingPeriod(customerPeriod.period, customerPeriod.customDateRange, activeActivity);

  const employeePeriod = useCascadingPeriod(selectedPeriod, customDateRange, selectedEmployeeId);

  const appointmentsPeriod = useCascadingPeriod(
    employeePeriod.period,
    employeePeriod.customDateRange,
    activeEmployeeActivity,
  );

  const orderBy = useOrderBy(sortColumn, sortDirection, columnKeyToOrderBy, ClientOrderBy.NameAsc);

  const {
    data: customers,
    hasNextPage,
    fetchNextPage,
    isPending,
    isFetching,
    totals,
    previousRange,
  } = useCustomersManagement({
    dateRange: effectiveDateRange,
    orderBy,
    searchTerm: debouncedSearchTerm || undefined,
    assignedEmployeeId,
    revenueRange,
    lastVisitBucket,
    lastVisitRange,
  });

  const comparisonWindowsLabel = useMemo(() => {
    if (!showTrends) return null;
    const current = formatComparisonRange(effectiveDateRange, locale);
    if (!previousRange) return `(${current})`;
    return tKpi("comparisonWindows", {
      current,
      previous: formatComparisonRange(previousRange, locale),
    });
  }, [showTrends, effectiveDateRange, previousRange, locale, tKpi]);

  const customersLengthRef = useRef(customers?.length);
  customersLengthRef.current = customers?.length;

  useEffect(() => {
    if (debouncedSearchTerm) {
      track(AnalyticsEvents.MV_SEARCH_CUSTOMER, {
        searchTerm: debouncedSearchTerm,
        resultCount: customersLengthRef.current,
      });
    }
  }, [debouncedSearchTerm, track]);

  const { data: selectedCustomer } = useCustomerById(selectedCustomerId ?? "");

  const openTodosData = useOpenTodosData({
    employeeId: selectedEmployeeId,
    todoTypes: MANAGEMENT_TODO_TYPES,
    employeeName: selectedEmployeeInfo?.name ?? "",
  });

  const selectedEmployee = useMemo(() => {
    if (!selectedEmployeeInfo) return null;
    return new TeamEmployee(
      selectedEmployeeInfo.id,
      selectedEmployeeInfo.name,
      null,
      new KpiValue(0, null),
      new KpiValue(0, null),
      new KpiValue(0, null),
      null,
    );
  }, [selectedEmployeeInfo]);

  const [selectedNote, setSelectedNote] = useState<Note | undefined>(undefined);

  const handleRowClick = (customerId: string) => {
    const customer = customers?.find((c) => c.id === customerId);
    track(AnalyticsEvents.MV_OPEN_CUSTOMER, {
      customerId,
      customerName: customer?.name ?? "",
    });
    setSelectedCustomerId(customerId);
  };

  const handleProductGroupOpen = (group: ProductGroupRef, metric: CustomerMetric) => {
    setActiveActivity("productGroup");
    setActivityContext(group.name);
    setLevel3ClientId(null);
    setProductDrillRoot({ group, initialMetric: metric });
  };

  const handleCompletedRecapsClick = () => {
    setActiveActivity("completedRecaps");
    setActivityContext(null);
    setLevel3ClientId(null);
  };

  const handleCompletedRecapAppointmentClick = (note: Note | undefined, clientId: string) => {
    setSelectedNote(note);
    setLevel3ClientId(clientId);
  };

  const handleCloseLevel3 = () => {
    setLevel3ClientId(null);
    setSelectedNote(undefined);
  };

  const handleCloseLevel2 = () => {
    setActiveActivity(null);
    setActivityContext(null);
    setLevel3ClientId(null);
  };

  const handleCloseLevel1 = () => {
    setSelectedCustomerId(null);
    setActiveActivity(null);
    setActivityContext(null);
    setLevel3ClientId(null);
  };

  const handleEmployeeClickFromCustomer = (employeeId: string, employeeName: string) => {
    setSelectedCustomerId(null);
    setActiveActivity(null);
    setActivityContext(null);
    setLevel3ClientId(null);
    setSelectedNote(undefined);
    setSelectedEmployeeInfo({ id: employeeId, name: employeeName });
  };

  const handleCustomerClickFromEmployee = (customerId: string, customerName: string) => {
    track(AnalyticsEvents.MV_OPEN_CUSTOMER, { customerId, customerName });
    setSelectedEmployeeInfo(null);
    setActiveEmployeeActivity(null);
    setEmployeeLevel3ClientId(null);
    setSelectedCustomerId(customerId);
  };

  const handleEmployeeActivityClick = (activity: ActivityType) => {
    setActiveEmployeeActivity(activity);
    setEmployeeLevel3ClientId(null);
  };

  const handleEmployeeLevel3Open = useCallback((clientId: string) => {
    setEmployeeLevel3ClientId(clientId);
  }, []);

  const handleCloseEmployeeLevel3 = () => {
    setEmployeeLevel3ClientId(null);
  };

  const handleCloseEmployeeLevel2 = () => {
    setActiveEmployeeActivity(null);
    setEmployeeLevel3ClientId(null);
  };

  const handleCloseEmployeeLevel1 = () => {
    setSelectedEmployeeInfo(null);
    setActiveEmployeeActivity(null);
    setEmployeeLevel3ClientId(null);
  };

  const handleEmployeeCompletedAppointmentClick = (_noteId: string | null, clientId: string) => {
    handleEmployeeLevel3Open(clientId);
  };

  const columns: Column[] = [
    {
      key: "name",
      label: t("columns.name"),
      type: "string",
      sortable: true,
      renderCell: (row) => (
        <CustomerNameCell name={row.name as string} externalReference={row.externalReference?.toString() ?? ""} />
      ),
    },
    {
      key: "revenue",
      label: t("columns.revenue"),
      type: "monetary",
      sortable: true,
    },
    {
      key: "profit",
      label: t("columns.margin"),
      type: "monetary",
      sortable: true,
    },
    {
      key: "orderFrequency",
      label: t("columns.orderFrequency"),
      type: "string",
      sortable: false,
    },
    {
      key: "employee",
      label: t("columns.employee"),
      type: "string",
      sortable: true,
    },
    {
      key: "lastVisit",
      label: t("columns.lastVisit"),
      type: "string",
      sortable: true,
    },
  ];

  const rows: Row[] = (customers ?? []).map((customer) => ({
    id: customer.id,
    name: customer.name,
    externalReference: customer.externalReference ?? "",
    revenue: {
      amount: formatCurrency(customer.revenue.totalValue, locale, currency),
      percentage: showTrends ? formatChange(customer.revenue.percentageChange) : "",
    },
    profit: {
      amount: formatProfitWithMargin(customer.profit.totalValue, customer.margin.totalValue, locale, currency),
      percentage: showTrends ? formatChange(customer.profit.percentageChange) : "",
    },
    orderFrequency:
      customer.meanDaysBetweenOrders !== null && customer.meanDaysBetweenOrders > 0
        ? t("columns.orderFrequencyValue", {
            days: Math.round(customer.meanDaysBetweenOrders),
          })
        : "",
    employee: customer.assignedEmployeeName ?? "",
    lastVisit: customer.lastVisitDate !== null ? formatDate(customer.lastVisitDate, locale) : "",
  }));

  const kpiHeaders: KPIHeaderColumn[] = useMemo(() => {
    if (!totals) return [];
    return [
      {
        title: t("columns.revenue"),
        value: formatCurrency(totals.revenue.totalValue, locale, currency),
        percentageChange: showTrends ? (totals.revenue.percentageChange ?? undefined) : undefined,
      },
      {
        title: t("columns.margin"),
        value:
          totals.revenue.totalValue === 0
            ? formatProfitWithMargin(0, 0, locale, currency)
            : formatProfitWithMargin(
                totals.profit.totalValue,
                totals.profit.totalValue / totals.revenue.totalValue,
                locale,
                currency,
              ),
        percentageChange: showTrends ? (totals.profit.percentageChange ?? undefined) : undefined,
      },
    ];
  }, [totals, locale, t, showTrends, currency]);

  return (
    <>
      <PaddingWrapper>
        <PageHeader
          title={t("title")}
          subtitle={showTrends ? subtitle : undefined}
          dateRangeLabel={comparisonWindowsLabel}
          showDateRangeSelector
          selectedPeriod={selectedPeriod}
          customDateRange={customDateRange}
          onPeriodChange={setSelectedPeriod}
          onCustomDateRangeChange={setCustomDateRange}
        />
      </PaddingWrapper>

      <Flex direction="column" gap={6}>
        <PaddingWrapper direction="column" gap={6}>
          {isFetching ? <KPIHeadersSkeleton /> : kpiHeaders.length > 0 && <KPIHeaders columns={kpiHeaders} />}
          {(isFetching || kpiHeaders.length > 0) && <Divider />}
          <ManagementSearchAndFilterBar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder={t("searchPlaceholder")}
            filterDropdowns={[]}
            additionalFilters={
              <CustomerFilterBar
                assignedEmployeeId={assignedEmployeeId}
                onAssignedEmployeeChange={setAssignedEmployeeId}
                revenueRange={revenueRange}
                onRevenueRangeChange={setRevenueRange}
                lastVisitBucket={lastVisitBucket}
                onLastVisitBucketChange={setLastVisitBucket}
                lastVisitRange={lastVisitRange}
                onLastVisitRangeChange={setLastVisitRange}
              />
            }
          />
        </PaddingWrapper>
        {isPending ? (
          <ManagementDataTableSkeleton
            columns={columns}
            rowCount={10}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
          />
        ) : (
          <ManagementDataTable
            columns={columns}
            rows={rows}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            hasMore={hasNextPage}
            onLoadMore={fetchNextPage}
            loadMoreLabel={t("loadMore")}
            isLoadingMore={isFetching}
            onRowClick={handleRowClick}
          />
        )}
      </Flex>

      {/* TODO: it would probably make sense to have the DateRangeSelector directly in the ManagementSidepanel as I think we will not be needing another type of component there -> so it makes no sense to keep it variable */}
      <ManagementSidepanel
        isOpen={selectedCustomerId !== null}
        onClose={handleCloseLevel1}
        level={1}
        isLevel1Shrunk={activeActivity !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={customerPeriod.period}
            customDateRange={customerPeriod.customDateRange}
            onPeriodChange={customerPeriod.setPeriod}
            onCustomDateRangeChange={customerPeriod.setCustomDateRange}
          />
        }
      >
        {selectedCustomerId && (
          <CustomerDetailsSidePanel
            clientId={selectedCustomerId}
            selectedPeriod={customerPeriod.period}
            selectedCustomDateRange={customerPeriod.customDateRange}
            onRecapCountClick={handleCompletedRecapsClick}
            onProductGroupOpen={handleProductGroupOpen}
            onEmployeeClick={handleEmployeeClickFromCustomer}
          />
        )}
      </ManagementSidepanel>

      <ProductDrilldownSidePanel
        isOpen={activeActivity === "productGroup" && activityContext !== null}
        onClose={handleCloseLevel2}
        isShrunk={level3ClientId !== null}
        root={productDrillRoot}
        clientId={selectedCustomerId}
        customerName={selectedCustomer?.name ?? ""}
        headerControls={
          <DateRangeSelector
            selectedPeriod={productGroupPeriod.period}
            customDateRange={productGroupPeriod.customDateRange}
            onPeriodChange={productGroupPeriod.setPeriod}
            onCustomDateRangeChange={productGroupPeriod.setCustomDateRange}
          />
        }
        selectedPeriod={productGroupPeriod.period}
        selectedCustomDateRange={productGroupPeriod.customDateRange}
      />

      <ManagementSidepanel
        isOpen={activeActivity === "completedRecaps"}
        onClose={handleCloseLevel2}
        level={2}
        isLevel2Shrunk={level3ClientId !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={recapsPeriod.period}
            customDateRange={recapsPeriod.customDateRange}
            onPeriodChange={recapsPeriod.setPeriod}
            onCustomDateRangeChange={recapsPeriod.setCustomDateRange}
          />
        }
      >
        {selectedCustomerId && selectedCustomer && (
          <CustomerRecapsSidePanel
            clientId={selectedCustomerId}
            customerName={selectedCustomer.name}
            selectedPeriod={recapsPeriod.period}
            selectedCustomDateRange={recapsPeriod.customDateRange}
            onRecapClick={handleCompletedRecapAppointmentClick}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel isOpen={level3ClientId !== null} onClose={handleCloseLevel3} level={3}>
        {level3ClientId && (
          <TodoSidePanel clientId={level3ClientId} note={selectedNote} onTodoToggle={() => undefined} />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel
        isOpen={selectedEmployeeId !== null}
        onClose={handleCloseEmployeeLevel1}
        level={1}
        isLevel1Shrunk={activeEmployeeActivity !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={employeePeriod.period}
            customDateRange={employeePeriod.customDateRange}
            onPeriodChange={employeePeriod.setPeriod}
            onCustomDateRangeChange={employeePeriod.setCustomDateRange}
          />
        }
      >
        {selectedEmployee && (
          <EmployeeSidePanel
            employee={selectedEmployee}
            locale={locale}
            onActivityClick={handleEmployeeActivityClick}
            onCustomerClick={handleCustomerClickFromEmployee}
            activeActivity={activeEmployeeActivity}
            selectedPeriod={employeePeriod.period}
            selectedCustomDateRange={employeePeriod.customDateRange}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel
        isOpen={selectedEmployeeId !== null && activeAppointmentState !== null}
        onClose={handleCloseEmployeeLevel2}
        level={2}
        isLevel2Shrunk={employeeLevel3ClientId !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={appointmentsPeriod.period}
            customDateRange={appointmentsPeriod.customDateRange}
            onPeriodChange={appointmentsPeriod.setPeriod}
            onCustomDateRangeChange={appointmentsPeriod.setCustomDateRange}
            excludedPeriods={[]}
          />
        }
      >
        {selectedEmployee && activeAppointmentState && (
          <EmployeeAppointmentStateSidePanel
            state={activeAppointmentState}
            employeeId={selectedEmployee.id}
            employeeName={selectedEmployee.name}
            selectedPeriod={appointmentsPeriod.period}
            selectedCustomDateRange={appointmentsPeriod.customDateRange}
            onAppointmentClick={
              activeAppointmentState === TeamMeetingState.Documented
                ? handleEmployeeCompletedAppointmentClick
                : undefined
            }
            onCustomerClick={handleCustomerClickFromEmployee}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel
        isOpen={selectedEmployeeId !== null && activeEmployeeActivity === "ignoredSignals"}
        onClose={handleCloseEmployeeLevel2}
        level={2}
        isLevel2Shrunk={employeeLevel3ClientId !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={appointmentsPeriod.period}
            customDateRange={appointmentsPeriod.customDateRange}
            onPeriodChange={appointmentsPeriod.setPeriod}
            onCustomDateRangeChange={appointmentsPeriod.setCustomDateRange}
            excludedPeriods={[]}
          />
        }
      >
        {selectedEmployee && (
          <DismissedCustomersSidePanel
            employeeId={selectedEmployee.id}
            employeeName={selectedEmployee.name}
            selectedPeriod={appointmentsPeriod.period}
            selectedCustomDateRange={appointmentsPeriod.customDateRange}
            onCustomerClick={handleCustomerClickFromEmployee}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel
        isOpen={selectedEmployeeId !== null && activeEmployeeActivity === "markedDone"}
        onClose={handleCloseEmployeeLevel2}
        level={2}
        isLevel2Shrunk={employeeLevel3ClientId !== null}
        headerControls={
          <DateRangeSelector
            selectedPeriod={appointmentsPeriod.period}
            customDateRange={appointmentsPeriod.customDateRange}
            onPeriodChange={appointmentsPeriod.setPeriod}
            onCustomDateRangeChange={appointmentsPeriod.setCustomDateRange}
            excludedPeriods={[]}
          />
        }
      >
        {selectedEmployee && (
          <EmployeeMarkedDoneSidePanel
            employeeId={selectedEmployee.id}
            employeeName={selectedEmployee.name}
            selectedPeriod={appointmentsPeriod.period}
            selectedCustomDateRange={appointmentsPeriod.customDateRange}
            onCustomerClick={handleCustomerClickFromEmployee}
          />
        )}
      </ManagementSidepanel>

      <ManagementSidepanel
        isOpen={selectedEmployeeId !== null && activeEmployeeActivity === "openTodos"}
        onClose={handleCloseEmployeeLevel2}
        level={2}
        isLevel2Shrunk={employeeLevel3ClientId !== null}
      >
        {openTodosData?.isPending ? (
          <OpenTodosSidePanelSkeleton />
        ) : openTodosData ? (
          <OpenTodosSidePanel data={openTodosData} onTodoClick={handleEmployeeLevel3Open} />
        ) : null}
      </ManagementSidepanel>

      <ManagementSidepanel isOpen={employeeLevel3ClientId !== null} onClose={handleCloseEmployeeLevel3} level={3}>
        {employeeLevel3ClientId && (
          <TodoSidePanel
            clientId={employeeLevel3ClientId}
            onTodoToggle={() => undefined}
            onCustomerClick={handleCustomerClickFromEmployee}
          />
        )}
      </ManagementSidepanel>
    </>
  );
};
